import React, { type PropsWithChildren, useCallback, useMemo } from 'react';

import {
  type ResetPasswordMutation,
  type ResetPasswordMutationVariables,
  type UserSessionQuery,
  type UserSessionQueryVariables,
  type ValidateResetPasswordTokenQuery,
  type ValidateResetPasswordTokenQueryVariables,
} from './__generated__/graphql';
import {
  AuthContext,
  type AuthContextState,
  type AuthenticationState,
  DEFAULT_LOGIN_DISPLAY_ERROR_MESSAGE,
  type LoginCredentials,
  type LoginResult,
} from './AuthContext';
import { type GraphQLClient, type GraphqlClientResponse, useGraphqlQuery } from './graphql';
import { parseAuthentication, type TrackError } from './helpers';
import {
  resetPasswordMutation,
  userSessionQuery,
  validateResetPasswordTokenQuery,
} from './queries';
import {
  logIn as logInRequest,
  register as registerRequest,
  requestHumanStatus as requestHumanStatusRequest,
  requestPasswordReset as requestPasswordResetRequest,
} from './sessionClient';

export {
  resetPasswordMutation,
  userSessionQuery,
  validateResetPasswordTokenQuery,
} from './queries';

export type AuthBaseUrlContextProviderProps = PropsWithChildren<{
  authBaseUrl: string;
  client: GraphQLClient;
  fetch?: typeof fetch;
  trackError?: TrackError;
}>;

const normalizeAuthBaseUrl = (authBaseUrl: string): string => authBaseUrl.replace(/\/+$/, '');

/** TODO: Add documentation */
export const AuthBaseUrlContextProvider: React.FC<AuthBaseUrlContextProviderProps> = ({
  trackError = () => {},
  client,
  fetch: fetchFn = fetch,
  authBaseUrl,
  children,
}) => {
  const resolvedAuthBaseUrl = normalizeAuthBaseUrl(authBaseUrl);

  // TODO: Check if we have cookie so we can possibly skip the network call.
  const queryResult = useGraphqlQuery<UserSessionQuery, UserSessionQueryVariables>(
    client,
    userSessionQuery,
    {},
  );

  const logout: AuthContextState['logout'] = useCallback(() => {
    return fetchFn(`${resolvedAuthBaseUrl}/session/sign-out`, {
      method: 'GET',
      credentials: 'include',
    })
      .then(() => {
        window.location.reload();
      })
      .catch((e) => {
        trackError('logout', e);
        window.location.assign(`${resolvedAuthBaseUrl}/logout`);
      });
  }, [fetchFn, resolvedAuthBaseUrl, trackError]);

  const validateResetPasswordToken = async (resetPasswordToken: string) => {
    try {
      const response: GraphqlClientResponse<ValidateResetPasswordTokenQuery> = await client.query<
        ValidateResetPasswordTokenQuery,
        ValidateResetPasswordTokenQueryVariables
      >(validateResetPasswordTokenQuery, {
        resetPasswordToken,
      });

      return {
        success: !!response?.data?.webPassword?.validateResetPasswordToken?.valid,
      };
    } catch (err) {
      return {
        success: false,
        displayErrorMessage: 'Error Validating Reset Password Token',
      };
    }
  };

  const resetPassword = async (token: string, password: string) => {
    try {
      const response: GraphqlClientResponse<ResetPasswordMutation> = await client.query<
        ResetPasswordMutation,
        ResetPasswordMutationVariables
      >(resetPasswordMutation, {
        resetPasswordToken: token,
        newPassword: password,
      });

      if (!response.error) {
        return {
          success: true,
        };
      }

      return {
        success: false,
      };
    } catch (err) {
      return {
        success: false,
      };
    }
  };

  const verifyLogin: AuthContextState['verifyLogin'] = useCallback(
    (credentials) => callVerifyLogin(resolvedAuthBaseUrl, fetchFn, trackError, credentials),
    [fetchFn, resolvedAuthBaseUrl, trackError],
  );

  const logIn: AuthContextState['logIn'] = useCallback(
    (credentials) => logInRequest(resolvedAuthBaseUrl, credentials, fetchFn),
    [fetchFn, resolvedAuthBaseUrl],
  );

  const register: AuthContextState['register'] = useCallback(
    (params) => registerRequest(resolvedAuthBaseUrl, params, fetchFn),
    [fetchFn, resolvedAuthBaseUrl],
  );

  const requestHumanStatus: AuthContextState['requestHumanStatus'] = useCallback(
    (email) => requestHumanStatusRequest(resolvedAuthBaseUrl, email, fetchFn),
    [fetchFn, resolvedAuthBaseUrl],
  );

  const requestPasswordReset: AuthContextState['requestPasswordReset'] = useCallback(
    (email) => requestPasswordResetRequest(resolvedAuthBaseUrl, email, fetchFn),
    [fetchFn, resolvedAuthBaseUrl],
  );

  const authentication: AuthenticationState = useMemo(
    () =>
      parseAuthentication(
        { data: queryResult.data, loading: queryResult.loading, error: queryResult.error },
        trackError,
      ),
    // trackError isn't needed here
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [queryResult.loading, queryResult.data, queryResult.error, logout],
  );

  return (
    <AuthContext.Provider
      value={{
        authentication,
        logout,
        refresh: queryResult.refresh,
        verifyLogin,
        validateResetPasswordToken,
        resetPassword,
        logIn,
        register,
        requestHumanStatus,
        requestPasswordReset,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

async function callVerifyLogin(
  authBaseUrl: string,
  fetchFn: typeof fetch,
  trackError: TrackError,
  credentials: LoginCredentials,
): Promise<LoginResult> {
  let payload: unknown;
  if ('googleIdToken' in credentials) {
    payload = { google_id_token: credentials.googleIdToken };
  } else if ('facebookIdToken' in credentials) {
    payload = { facebook_id_token: credentials.facebookIdToken };
  } else {
    payload = {
      human: {
        email: credentials.email,
        password: credentials.password,
      },
    };
  }

  // See `verify_login` at
  // https://github.com/opendoor-labs/web/blob/master/app/controllers/humans/sessions_controller.rb
  let resp;
  try {
    resp = await fetchFn(`${authBaseUrl}/verify_login`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });
  } catch (e) {
    trackError('login', e as Error);
    return { success: false, displayErrorMessage: DEFAULT_LOGIN_DISPLAY_ERROR_MESSAGE };
  }

  if (resp.ok) {
    return { success: true };
  } else {
    let displayErrorMessage = DEFAULT_LOGIN_DISPLAY_ERROR_MESSAGE;
    try {
      const json = await resp.json();
      if ('error' in json) {
        displayErrorMessage = json['error'];
      }
    } catch (e) {
      trackError('login error without error message', e as Error);
    }
    return {
      success: false,
      displayErrorMessage,
    };
  }
}
